import { Button } from '@/components/base/button'
import { Dialog, DialogActions, DialogBody, DialogTitle, ShowProps } from '@/components/base/dialog'
import { Divider } from '@/components/base/divider'
import { useEffect, useState } from 'react'
import { AssetQuery } from './__generated__/show_query'
import { Query } from './show_query'
import { useQuery } from '@apollo/client'
import { Inline as Loading } from '@/components/loading'
import { Inline as Error } from '@/components/error'
import { ShowDialog as AssetTypeShowDialog } from '@/components/asset_type/show_dialog'
import { Accordion } from '../accordion'
import { Table as MaintenanceEventsTable } from '@/components/maintenance_event/table'
import { Table as DocumentsTable } from '@/components/document/table'
import { EditDialog } from './edit_dialog'
import { EditDialog as EditBarcodeDialog } from '../barcode/edit_dialog'
import { Subheading } from '../base/heading'
import { PencilIcon } from '@heroicons/react/24/solid'
import { Text } from '@/components/base/text'
import { AttachmentResourceType, DocumentSubjectType } from '@/types'
import { Collection as AttachmentsCollection } from '@/components/attachment/collection'

type Asset = AssetQuery['assets']['nodes'][0]

export const ShowDialog = ({ id, open, setOpen }: ShowProps) => {
  const [openEdit, setOpenEdit] = useState(false)
  const [openEditBarcode, setOpenEditBarcode] = useState(false)
  const [openAssetType, setOpenAssetType] = useState(false)
  const [asset, setAsset] = useState<Asset | null>(null)

  const { data, loading, error } = useQuery<AssetQuery>(Query, { variables: { filters: { idEq: id } } })

  useEffect(() => {
    if (!data || !id) { return }

    setAsset(data.assets.nodes[0])
  }, [id, data])

  const onEdit = () => {
    setOpenEdit(true)
  }

  const onEditBarcode = () => {
    setOpenEditBarcode(true)
  }

  return (
    <>
      <Dialog open={open} size="7xl" onClose={setOpen}>
        <DialogTitle>
          {
            loading || !asset
              ? <Loading />
              : <div className="flex flex-row justify-between">
                <div>
                  <span className="flex flex-row gap-4 items-center pb-6">
                    { asset.name }
                  </span>
                  { data && data.assets.nodes[0].notes && (
                    <span className="flex flex-row gap-2 items-center">
                      <PencilIcon className="h-6 w-6" />
                      <Subheading>Notes</Subheading>
                      <Text>{ data.assets.nodes[0].notes }</Text>
                    </span>
                  )}
                </div>
              </div>
          }
        </DialogTitle>
        <DialogBody>
          { error && <Error message={error.message} />}
          { asset && <div className="flex flex-col gap-4">
            <span className="flex flex-row gap-2 items-center">
              <Button onClick={() => setOpenAssetType(true)}>
                View {data?.assets.nodes[0].assetType?.name}
              </Button>
            </span>
            <Divider />
            {/* <Accordion title="Stats">Something should be here...</Accordion> */}
            <Accordion title="Maintenance history">
              <MaintenanceEventsTable filters={{ assetIdEq: asset.id }} hiddenColumns={['Asset']} fixedHeight />
            </Accordion>
            <Accordion title="Attachments">
              <AttachmentsCollection
                filters={{ nameEq: 'attachments', resourceIdEq: asset.id, resourceTypeEq: AttachmentResourceType.Asset }}
                pageSize={10}
                fixedHeight
              />
            </Accordion>
            <Accordion title="Documents">
              <DocumentsTable filters={{ subjectIdEq: asset.id, subjectTypeEq: DocumentSubjectType.Asset }} pageSize={20} fixedHeight />
            </Accordion>
          </div> }
        </DialogBody>
        <DialogActions>
          { asset && <Button color="amber" onClick={onEditBarcode}>Edit barcode</Button> }
          { asset && <Button color="amber" onClick={onEdit}>Edit</Button> }
        </DialogActions>
      </Dialog>
      { data?.assets.nodes[0].assetType.id &&
        <AssetTypeShowDialog
          id={data?.assets.nodes[0].assetType.id}
          open={openAssetType}
          setOpen={setOpenAssetType}
        /> }
      { asset && <EditDialog id={asset.id} open={openEdit} setOpen={setOpenEdit} /> }
      { asset && <EditBarcodeDialog id={asset.barcode.id} open={openEditBarcode} setOpen={setOpenEditBarcode} /> }
    </>
  )
}
